import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, FileClock, FileUp, Languages, Paperclip, Pencil, Plus, RefreshCw, ShieldCheck, Sparkles, Trash2, Upload } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/page-header';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Textarea,
  useToast,
} from '@/components/ui';
import { SupplierDialog } from '@/features/suppliers/suppliers-page';
import { SUPPLIERS_KEY, suppliersApi } from '@/features/suppliers/suppliers-api';
import { ApiError, openAuthedFile } from '@/lib/api';
import { intlLocale } from '@/lib/i18n';
import { cn, formatCurrency } from '@/lib/utils';
import {
  CATEGORIES,
  CONTRACT_FILE_ACCEPT,
  CONTRACT_FILE_MAX,
  CONTRACTS_KEY,
  contractsApi,
  isCategory,
  monthlyCost,
  type Contract,
  type ContractExtraction,
  type ContractInput,
} from './contracts-api';
import { useCanEdit } from '@/lib/auth';

const dateFmt = new Intl.DateTimeFormat(intlLocale(), { timeZone: 'UTC', day: 'numeric', month: 'numeric', year: 'numeric' });
const fmt = (d: string | null) => (d ? dateFmt.format(new Date(`${d}T12:00:00Z`)) : '—');
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem' }).format(new Date());
const daysUntil = (d: string) => Math.round((Date.parse(`${d}T12:00:00Z`) - Date.parse(`${today()}T12:00:00Z`)) / 86_400_000);

// התווית: contracts:state.<מצב> — מתורגמת בתצוגה.
const STATE: Record<Contract['state'], { tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info'; order: number }> = {
  decide_soon: { tone: 'warning', order: 0 },
  notice_passed: { tone: 'danger', order: 1 },
  expired: { tone: 'neutral', order: 4 },
  active: { tone: 'success', order: 2 },
  open_ended: { tone: 'info', order: 3 },
};

/** שם הסוג בשפת הממשק; סוג לא מוכר — כמו שהוא. */
const categoryLabel = (t: TFunction<['contracts', 'common']>, c: string) => (isCategory(c) ? t(`categories.${c}`) : c);

/** חוזים ושירותים: מדפסות, אינטרנט, כיבוד, מכונת מים — תוקף, חידוש, אחריות. */
export function ContractsPage() {
  const canEdit = useCanEdit();
  const { t } = useTranslation(['contracts', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: CONTRACTS_KEY, queryFn: ({ signal }) => contractsApi.list(signal) });
  const [editing, setEditing] = useState<Contract | 'new' | null>(null);
  // "חוזה מקובץ": הקובץ נבחר מהכפתור (צריך לחיצה של המשתמש), והחלון נפתח וקורא אותו.
  const [fromFile, setFromFile] = useState<File | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  const remove = useMutation({
    mutationFn: (id: string) => contractsApi.remove(id),
    onSuccess: async () => {
      toast.success(t('page.removed'));
      await qc.invalidateQueries({ queryKey: CONTRACTS_KEY });
    },
    onError: (e) => toast.error(t('common:errors.removeFailed'), e instanceof ApiError ? e.message : undefined),
  });

  const items = useMemo(
    () =>
      [...(list.data ?? [])].sort(
        (a, b) => STATE[a.state].order - STATE[b.state].order || (a.noticeDate ?? '9999').localeCompare(b.noticeDate ?? '9999'),
      ),
    [list.data],
  );
  const live = items.filter((c) => c.state !== 'expired');
  const monthly = live.reduce((sum, c) => sum + monthlyCost(c), 0);
  const attention = items.filter((c) => c.state === 'decide_soon' || c.state === 'notice_passed').length;
  const warranties = items.filter((c) => c.warrantyUntil && daysUntil(c.warrantyUntil) >= 0 && daysUntil(c.warrantyUntil) <= 30).length;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        action={
          canEdit ? (
            <div className="flex gap-2">
              <input
                ref={picker}
                type="file"
                accept={CONTRACT_FILE_ACCEPT}
                className="sr-only"
                aria-label={t('page.pickFile')}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) {
                    setFromFile(f);
                    setEditing('new');
                  }
                }}
              />
              <Button size="sm" variant="secondary" onClick={() => picker.current?.click()}>
                <FileUp aria-hidden />
                {t('page.fromFile')}
              </Button>
              <Button size="sm" onClick={() => setEditing('new')} data-track="contract.new">
                <Plus aria-hidden />
                {t('page.new')}
              </Button>
            </div>
          ) : undefined
        }
      />

      {list.data && list.data.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label={t('stats.monthlyCost')} value={formatCurrency(monthly)} hint={t('stats.activeContracts', { count: live.length })} />
          <Stat label={t('stats.decide')} value={String(attention)} hint={t('stats.decideHint')} tone={attention ? 'warning' : undefined} />
          <Stat label={t('stats.warrantyEnding')} value={String(warranties)} hint={t('stats.warrantyHint')} tone={warranties ? 'warning' : undefined} />
        </div>
      ) : null}

      <Card>
        {list.isPending ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={FileClock}
            title={t('page.empty')}
            description={t('page.emptyHint')}
            action={
              canEdit ? (
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => picker.current?.click()}>
                    <FileUp aria-hidden />
                    {t('page.fromFile')}
                  </Button>
                  <Button size="sm" onClick={() => setEditing('new')}>
                    <Plus aria-hidden />
                    {t('page.first')}
                  </Button>
                </div>
              ) : undefined
            }
          />
        ) : (
          <ul className="divide-y divide-border">
            {items.map((c) => (
              <li key={c.id} className={cn('flex flex-wrap items-start gap-3 px-4 py-3', c.state === 'expired' && 'opacity-60')}>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-fg">{c.title}</span>
                    <Badge tone={STATE[c.state].tone}>{t(`state.${c.state}`)}</Badge>
                    {c.hasFile ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-2xs text-fg-subtle hover:text-accent"
                        aria-label={t('page.openFileAria', { title: c.title })}
                        onClick={() => void openAuthedFile(`/contracts/${c.id}/file`)}
                      >
                        <Paperclip className="size-3" aria-hidden />
                        {t('page.file')}
                      </button>
                    ) : null}
                    {c.autoRenew ? (
                      <span className="inline-flex items-center gap-1 text-2xs text-fg-subtle">
                        <RefreshCw className="size-3" aria-hidden />
                        {t('page.autoRenews')}
                      </span>
                    ) : null}
                  </p>
                  <p className="text-xs text-fg-muted">
                    {categoryLabel(t, c.category)}
                    {c.supplier ? ` · ${c.supplier.name}` : ''}
                    {c.cost ? ` · ${formatCurrency(Number(c.cost))}${c.costPeriod === 'MONTHLY' || c.costPeriod === 'YEARLY' ? ` ${t(`period.${c.costPeriod}`)}` : ''}` : ''}
                  </p>
                  <p className="flex flex-wrap gap-x-4 gap-y-0.5 text-2xs text-fg-subtle">
                    {c.endDate ? <span>{t('page.until', { date: fmt(c.endDate) })}</span> : null}
                    {c.noticeDate && c.state !== 'expired' ? (
                      <span className={cn('inline-flex items-center gap-1', c.state === 'decide_soon' && 'font-medium text-warning')}>
                        <AlarmClock className="size-3" aria-hidden />
                        {t('page.noticeBy', { date: fmt(c.noticeDate) })}
                        {c.state === 'decide_soon' ? t('page.daysLeft', { count: daysUntil(c.noticeDate) }) : ''}
                      </span>
                    ) : null}
                    {c.warrantyUntil ? (
                      <span className="inline-flex items-center gap-1">
                        <ShieldCheck className="size-3" aria-hidden />
                        {t('page.warrantyUntil', { date: fmt(c.warrantyUntil) })}
                      </span>
                    ) : null}
                  </p>
                </div>
                {canEdit ? (
                  <>
                    <Button variant="ghost" size="sm" aria-label={t('page.editAria', { title: c.title })} onClick={() => setEditing(c)}>
                      <Pencil aria-hidden />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('page.removeAria', { title: c.title })}
                      loading={remove.isPending && remove.variables === c.id}
                      onClick={() => {
                        if (window.confirm(t('page.confirmRemove', { title: c.title }))) remove.mutate(c.id);
                      }}
                    >
                      <Trash2 aria-hidden />
                    </Button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
      {editing ? (
        <ContractDialog
          contract={editing === 'new' ? null : editing}
          initialFile={editing === 'new' ? fromFile : null}
          onClose={() => {
            setEditing(null);
            setFromFile(null);
          }}
        />
      ) : null}
    </div>
  );
}

function Stat({ label, value, hint, tone }: { label: string; value: string; hint: string; tone?: 'warning' }) {
  return (
    <Card>
      <CardContent className="space-y-0.5 py-4">
        <p className="text-xs text-fg-muted">{label}</p>
        <p className={cn('tabular text-xl font-semibold text-fg', tone === 'warning' && 'text-warning')}>{value}</p>
        <p className="text-2xs text-fg-subtle">{hint}</p>
      </CardContent>
    </Card>
  );
}

const NONE = '__none__';

type FormState = {
  title: string;
  category: string;
  supplierId: string;
  startDate: string;
  endDate: string;
  autoRenew: boolean;
  noticeDays: string;
  cost: string;
  costPeriod: 'MONTHLY' | 'YEARLY' | 'ONE_TIME';
  warrantyUntil: string;
  notes: string;
  summary: string;
};

// שמות השפות: contracts:languages.<קוד> — מתורגמים בתצוגה.
const LANGUAGES = ['en', 'ar', 'ru', 'fr', 'de', 'es'] as const;
const isLanguage = (l: string): l is (typeof LANGUAGES)[number] => (LANGUAGES as readonly string[]).includes(l);

/**
 * מה שנקרא מהקובץ → הטופס. חוזה חדש: כל מה שנמצא. חוזה קיים: רק שדות
 * ריקים — לא דורסים מה שהבעלים כבר כתב.
 */
function applyExtraction(v: FormState, ex: ContractExtraction, isNew: boolean, t: TFunction<['contracts', 'common']>): { next: FormState; filled: Set<keyof FormState> } {
  const e = ex.extracted;
  const next = { ...v };
  const filled = new Set<keyof FormState>();
  const put = <K extends keyof FormState>(k: K, value: FormState[K] | null | undefined, empty: boolean) => {
    if (value === null || value === undefined || value === '') return;
    if (!isNew && !empty) return;
    next[k] = value;
    filled.add(k);
  };
  // בלי כותרת בעברית (חוזה באנגלית, בלי קריאה חכמה) — סוג + ספק.
  const cat = isCategory(e.category) ? t(`categories.${e.category}`) : null;
  const supplierName = ex.supplier?.name ?? e.supplierName;
  put('title', e.title ?? (cat && e.category !== 'other' ? `${cat}${supplierName ? ` — ${supplierName}` : ''}` : null), !v.title.trim());
  put('category', isCategory(e.category) ? e.category : null, v.category === 'other');
  put('supplierId', ex.supplier?.id, v.supplierId === NONE);
  put('startDate', e.startDate, !v.startDate);
  put('endDate', e.endDate, !v.endDate);
  if (e.autoRenew !== null && (isNew || !v.autoRenew)) {
    next.autoRenew = e.autoRenew;
    filled.add('autoRenew');
  }
  put('noticeDays', e.noticeDays != null ? String(e.noticeDays) : null, v.noticeDays === '30' || !v.noticeDays);
  put('cost', e.cost != null ? String(e.cost) : null, !v.cost);
  put('costPeriod', e.costPeriod, !v.cost);
  put('warrantyUntil', e.warrantyUntil, !v.warrantyUntil);
  put('summary', e.summary, !v.summary.trim());
  return { next, filled };
}

function ContractDialog({ contract, initialFile, onClose }: { contract: Contract | null; initialFile?: File | null; onClose: () => void }) {
  const { t } = useTranslation(['contracts', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const suppliers = useQuery({ queryKey: SUPPLIERS_KEY, queryFn: ({ signal }) => suppliersApi.list(signal) });
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [v, setV] = useState<FormState>({
    title: contract?.title ?? '',
    category: contract?.category ?? 'other',
    supplierId: contract?.supplierId ?? NONE,
    startDate: contract?.startDate ?? '',
    endDate: contract?.endDate ?? '',
    autoRenew: contract?.autoRenew ?? false,
    noticeDays: String(contract?.noticeDays ?? 30),
    cost: contract?.cost ?? '',
    costPeriod: contract?.costPeriod ?? 'MONTHLY',
    warrantyUntil: contract?.warrantyUntil ?? '',
    notes: contract?.notes ?? '',
    summary: contract?.summary ?? '',
  });
  const set = <K extends keyof FormState>(k: K, x: FormState[K]) => {
    setV((p) => ({ ...p, [k]: x }));
    setFilled((f) => {
      if (!f.has(k)) return f;
      const next = new Set(f);
      next.delete(k);
      return next;
    });
  };
  const [file, setFile] = useState<File | null>(null);
  const [extraction, setExtraction] = useState<ContractExtraction | null>(null);
  const [filled, setFilled] = useState<Set<keyof FormState>>(new Set());
  const input = useRef<HTMLInputElement>(null);
  const datesInvalid = !!v.startDate && !!v.endDate && v.endDate < v.startDate;
  const costInvalid = v.cost !== '' && !(Number(v.cost) >= 0);

  const read = useMutation({
    mutationFn: (f: File) => contractsApi.extract(f),
    onSuccess: (ex) => {
      setExtraction(ex);
      setV((cur) => {
        const { next, filled: got } = applyExtraction(cur, ex, !contract, t);
        setFilled(got);
        return next;
      });
    },
    onError: (e) => toast.error(t('dialog.readFailed'), e instanceof ApiError ? t('dialog.readFailedDetail', { message: e.message }) : undefined),
  });

  const pick = (f: File | undefined) => {
    if (!f) return;
    if (f.size > CONTRACT_FILE_MAX) {
      toast.error(t('dialog.fileTooLarge'));
      return;
    }
    setFile(f);
    setExtraction(null);
    read.mutate(f);
  };

  // נפתח מ"חוזה מקובץ" — קוראים מיד.
  const started = useRef(false);
  useEffect(() => {
    if (initialFile && !started.current) {
      started.current = true;
      pick(initialFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFile]);

  const save = useMutation({
    mutationFn: async () => {
      const body: ContractInput = {
        title: v.title.trim(),
        category: v.category,
        supplierId: v.supplierId === NONE ? null : v.supplierId,
        startDate: v.startDate || null,
        endDate: v.endDate || null,
        autoRenew: v.autoRenew,
        noticeDays: Math.min(365, Math.max(0, Math.round(Number(v.noticeDays) || 0))),
        cost: v.cost === '' ? null : Math.round(Number(v.cost) * 100) / 100,
        costPeriod: v.cost === '' ? null : v.costPeriod,
        warrantyUntil: v.warrantyUntil || null,
        notes: v.notes.trim() || null,
        summary: v.summary.trim() || null,
      };
      const saved = contract ? await contractsApi.update(contract.id, body) : await contractsApi.create(body);
      if (file) await contractsApi.attachFile(saved.id, file);
      return saved;
    },
    onSuccess: async () => {
      toast.success(contract ? t('dialog.updated') : t('dialog.added'), v.endDate ? t('dialog.remindBeforeNotice') : undefined);
      await qc.invalidateQueries({ queryKey: CONTRACTS_KEY });
      await qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
      onClose();
    },
    onError: (e) => toast.error(t('common:errors.saveFailed'), e instanceof ApiError ? e.message : undefined),
  });

  const ex = extraction?.extracted;
  const translatedFrom = ex?.language && ex.language !== 'he' && extraction?.source === 'ai' ? (isLanguage(ex.language) ? t(`languages.${ex.language}`) : ex.language) : null;
  const supplierSuggestion = extraction && !extraction.supplier && ex?.supplierName && v.supplierId === NONE ? ex : null;
  const mark = (k: keyof FormState) => (filled.has(k) ? 'ring-2 ring-accent/40' : undefined);

  return (
    <>
      <Dialog open={!addingSupplier} onOpenChange={(open) => !open && onClose()}>
        <DialogContent aria-describedby="contract-desc" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{contract ? t('dialog.editTitle', { title: contract.title }) : t('dialog.newTitle')}</DialogTitle>
            <DialogDescription id="contract-desc">
              {t('dialog.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <input
                ref={input}
                type="file"
                accept={CONTRACT_FILE_ACCEPT}
                className="sr-only"
                aria-label={t('page.pickFile')}
                onChange={(e) => {
                  pick(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => input.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  pick(e.dataTransfer.files[0]);
                }}
                className="flex w-full items-center gap-3 rounded-(--radius-lg) border border-dashed border-border-strong bg-surface-sunken px-4 py-3 text-start transition-colors hover:border-accent hover:bg-accent-subtle focus-visible:outline-2 focus-visible:outline-accent"
              >
                <Upload className="size-5 shrink-0 text-fg-subtle" aria-hidden />
                <span className="min-w-0 flex-1">
                  {read.isPending ? (
                    <span className="block text-sm text-fg-muted">{t('dialog.reading', { name: file?.name })}</span>
                  ) : (
                    <>
                      <span className="block truncate text-sm font-medium text-fg">
                        {file ? file.name : contract?.hasFile ? t('dialog.attachedReplace', { name: contract.fileName ?? t('dialog.defaultFileName') }) : t('dialog.upload')}
                      </span>
                      <span className="block text-xs text-fg-muted">{t('dialog.uploadHint')}</span>
                    </>
                  )}
                </span>
              </button>
              {contract?.hasFile && !file ? (
                <Button type="button" variant="link" size="sm" onClick={() => void openAuthedFile(`/contracts/${contract.id}/file`)}>
                  <Paperclip aria-hidden />
                  {t('dialog.openAttached')}
                </Button>
              ) : null}
              {extraction ? (
                <div aria-live="polite" className="space-y-1.5">
                  {extraction.source !== 'none' ? (
                    <p className="flex flex-wrap items-center gap-2 rounded-(--radius-md) bg-accent-subtle px-3 py-2 text-xs text-fg">
                      {translatedFrom ? <Languages className="size-3.5 text-accent" aria-hidden /> : <Sparkles className="size-3.5 text-accent" aria-hidden />}
                      {filled.size
                        ? t('dialog.filled', { count: filled.size, translated: translatedFrom ? t('dialog.translatedFrom', { language: translatedFrom }) : '' })
                        : contract
                          ? t('dialog.alreadyFilled')
                          : t('dialog.nothingFound')}
                    </p>
                  ) : null}
                  {extraction.warnings.map((w) => (
                    <p key={w} className="rounded-(--radius-md) bg-warning-subtle px-3 py-2 text-xs text-warning">
                      {w}
                    </p>
                  ))}
                  {supplierSuggestion ? (
                    <p className="flex flex-wrap items-center justify-between gap-2 rounded-(--radius-md) border border-border px-3 py-2 text-xs text-fg">
                      <span>
                        {t('dialog.supplierInContract')} <strong>{supplierSuggestion.supplierName}</strong>
                        {supplierSuggestion.supplierTaxId ? t('dialog.supplierTaxId', { taxId: supplierSuggestion.supplierTaxId }) : ''}
                        {t('dialog.supplierNotFound')}
                      </span>
                      <Button type="button" size="sm" variant="secondary" onClick={() => setAddingSupplier(true)}>
                        <Plus aria-hidden />
                        {t('dialog.addSupplier')}
                      </Button>
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
            <Field label={t('dialog.what')} htmlFor="ct-title" required className="sm:col-span-2">
              <Input id="ct-title" autoFocus={!initialFile} maxLength={200} placeholder={t('dialog.whatPlaceholder')} className={mark('title')} value={v.title} onChange={(e) => set('title', e.target.value)} />
            </Field>
            <Field label={t('dialog.category')} htmlFor="ct-category">
              <Select value={v.category} onValueChange={(x) => set('category', x)}>
                <SelectTrigger id="ct-category" aria-label={t('dialog.category')} className={mark('category')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((k) => (
                    <SelectItem key={k} value={k}>
                      {t(`categories.${k}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t('dialog.supplier')} htmlFor="ct-supplier">
              <div className="flex gap-2">
                <Select value={v.supplierId} onValueChange={(x) => set('supplierId', x)}>
                  <SelectTrigger id="ct-supplier" aria-label={t('dialog.supplier')} className={cn('flex-1', mark('supplierId'))}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('dialog.noSupplier')}</SelectItem>
                    {suppliers.data?.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="secondary" size="sm" aria-label={t('dialog.newSupplier')} onClick={() => setAddingSupplier(true)}>
                  <Plus aria-hidden />
                </Button>
              </div>
            </Field>
            <Field label={t('dialog.from')} htmlFor="ct-start">
              <Input id="ct-start" type="date" dir="ltr" className={mark('startDate')} value={v.startDate} onChange={(e) => set('startDate', e.target.value)} />
            </Field>
            <Field label={t('dialog.to')} htmlFor="ct-end" error={datesInvalid ? t('dialog.endBeforeStart') : undefined} hint={t('dialog.toHint')}>
              <Input id="ct-end" type="date" dir="ltr" className={mark('endDate')} value={v.endDate} min={v.startDate || undefined} onChange={(e) => set('endDate', e.target.value)} />
            </Field>
            <Field label={t('dialog.noticeDays')} htmlFor="ct-notice" hint={t('dialog.noticeDaysHint')}>
              <Input id="ct-notice" type="number" inputMode="numeric" dir="ltr" min={0} max={365} className={mark('noticeDays')} value={v.noticeDays} onChange={(e) => set('noticeDays', e.target.value)} />
            </Field>
            <label className={cn('flex items-center justify-between gap-3 self-end rounded-(--radius-md) border border-border px-3 py-2.5', mark('autoRenew'))}>
              <span>
                <span className="block text-sm font-medium text-fg">{t('dialog.autoRenews')}</span>
                <span className="block text-2xs text-fg-muted">{t('dialog.autoRenewsHint')}</span>
              </span>
              <Switch checked={v.autoRenew} onCheckedChange={(x) => set('autoRenew', x)} aria-label={t('dialog.autoRenews')} />
            </label>
            <Field label={t('dialog.cost')} htmlFor="ct-cost" error={costInvalid ? t('dialog.invalidAmount') : undefined}>
              <div className="flex gap-2">
                <Input id="ct-cost" type="number" inputMode="decimal" dir="ltr" min={0} step="0.01" className={cn('flex-1', mark('cost'))} value={v.cost} onChange={(e) => set('cost', e.target.value)} />
                <Select value={v.costPeriod} onValueChange={(x) => set('costPeriod', x as FormState['costPeriod'])}>
                  <SelectTrigger aria-label={t('dialog.costPeriod')} className={cn('w-28', mark('costPeriod'))}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MONTHLY">{t('period.MONTHLY')}</SelectItem>
                    <SelectItem value="YEARLY">{t('period.YEARLY')}</SelectItem>
                    <SelectItem value="ONE_TIME">{t('period.ONE_TIME')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Field>
            <Field label={t('dialog.warrantyUntil')} htmlFor="ct-warranty" hint={t('dialog.warrantyHint')}>
              <Input id="ct-warranty" type="date" dir="ltr" className={mark('warrantyUntil')} value={v.warrantyUntil} onChange={(e) => set('warrantyUntil', e.target.value)} />
            </Field>
            <Field label={t('dialog.summary')} htmlFor="ct-summary" className="sm:col-span-2" hint={translatedFrom ? t('dialog.summaryTranslatedHint', { language: translatedFrom }) : t('dialog.summaryHint')}>
              <Textarea id="ct-summary" rows={v.summary ? 5 : 2} maxLength={4000} placeholder={t('dialog.summaryPlaceholder')} className={mark('summary')} value={v.summary} onChange={(e) => set('summary', e.target.value)} />
            </Field>
            <Field label={t('dialog.notes')} htmlFor="ct-notes" className="sm:col-span-2">
              <Textarea id="ct-notes" rows={2} maxLength={4000} placeholder={t('dialog.notesPlaceholder')} value={v.notes} onChange={(e) => set('notes', e.target.value)} />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button disabled={!v.title.trim() || datesInvalid || costInvalid || read.isPending} loading={save.isPending} onClick={() => save.mutate()}>
              {t('common:actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {addingSupplier ? (
        <SupplierDialog
          supplier={null}
          initial={
            supplierSuggestion
              ? {
                  name: supplierSuggestion.supplierName ?? '',
                  taxId: supplierSuggestion.supplierTaxId ?? '',
                  email: supplierSuggestion.supplierEmail ?? '',
                  phone: supplierSuggestion.supplierPhone ?? '',
                  contactName: supplierSuggestion.contactName ?? '',
                }
              : undefined
          }
          onClose={() => setAddingSupplier(false)}
          onSaved={(s) => set('supplierId', s.id)}
        />
      ) : null}
    </>
  );
}
